import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useFormLocation } from '../FormLocationContext';
import { FieldShell } from './FieldShell';
import type { FieldComponentProps } from './types';

/**
 * Pre-existing values may carry an `OTHER:` prefix from an older free-text
 * version of this widget. The free-text input is gone, but we still strip
 * the prefix on display so historical submissions read sensibly.
 */
const OTHER_PREFIX = 'OTHER:';

const splitMulti = (raw: unknown): string[] => {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const stripOtherPrefix = (value: string): string =>
  value.startsWith(OTHER_PREFIX) ? value.slice(OTHER_PREFIX.length) || value : value;

export const LocationField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const allowMultiple = field.constraints?.allow_multiple === true;
  const locationSource = useFormLocation();

  // --- Cascade state -------------------------------------------------------
  const [district, setDistrict] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [villageId, setVillageId] = useState('');

  const districtsQuery = useQuery({
    queryKey: ['onboarding-location-districts'],
    queryFn: () => locationSource.getDistricts(),
    staleTime: 10 * 60 * 1000,
  });

  const subdistrictsQuery = useQuery({
    queryKey: ['onboarding-location-subdistricts', district],
    queryFn: () => locationSource.getSubdistricts(district),
    enabled: !!district,
    staleTime: 10 * 60 * 1000,
  });

  const villagesQuery = useQuery({
    queryKey: ['onboarding-location-villages', district, subdistrict],
    queryFn: () => locationSource.getVillages(district, subdistrict),
    enabled: !!district && !!subdistrict,
    staleTime: 10 * 60 * 1000,
  });

  const districtNames = useMemo(() => districtsQuery.data ?? [], [districtsQuery.data]);

  const subdistrictNames = useMemo(() => {
    const list = subdistrictsQuery.data ?? [];
    return list.map((s) => s.subdistrict_name).filter(Boolean);
  }, [subdistrictsQuery.data]);

  const villages = useMemo(() => villagesQuery.data ?? [], [villagesQuery.data]);

  // --- Multi-select chip list ---------------------------------------------
  const selectedValues = allowMultiple ? splitMulti(value) : [];

  // --- Single-mode: hydrate the village id from any existing value --------
  useEffect(() => {
    if (allowMultiple) return;
    if (typeof value !== 'string' || value === '') return;
    setVillageId(stripOtherPrefix(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Reverse-lookup district + subdistrict from the prefilled village id
  // (single-mode only). Without this, the cascade selectors stay empty when
  // an existing submission carries just a `master_location_lookup_id` and
  // the user has no idea what village is currently selected. Failure modes
  // (no lookup method, network error) leave the cascade empty — the village
  // id is still preserved as the submitted value.
  useEffect(() => {
    if (allowMultiple) return;
    if (!locationSource.getVillageByLgd) return;
    if (typeof value !== 'string' || value === '') return;
    if (district || subdistrict) return; // already hydrated / user-edited
    const code = stripOtherPrefix(value);
    let cancelled = false;
    locationSource
      .getVillageByLgd(code)
      .then((result) => {
        if (cancelled || !result) return;
        const d = result.district_name_in_english;
        const sd = result.subdistrict_name_in_english;
        if (typeof d === 'string' && d.length > 0) setDistrict(d);
        if (typeof sd === 'string' && sd.length > 0) setSubdistrict(sd);
      })
      .catch(() => {
        // Lookup failure leaves cascade empty; village id is still emitted
        // by the existing single-mode emit effect.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locationSource]);

  // --- Single-mode: emit value when cascade resolves ----------------------
  useEffect(() => {
    if (allowMultiple) return;
    if (villageId && villageId !== value) onChange(villageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMultiple, villageId]);

  // --- Multi-mode helpers -------------------------------------------------
  const addCurrent = () => {
    if (!villageId) return;
    if (selectedValues.includes(villageId)) return;
    const next = [...selectedValues, villageId].join(',');
    onChange(next);
    setVillageId('');
  };

  const removeAt = (idx: number) => {
    const next = selectedValues.filter((_, i) => i !== idx).join(',');
    onChange(next);
  };

  const labelForChip = (v: string): string => {
    const stripped = stripOtherPrefix(v);
    const known = villages.find((x) => x.village_id === stripped);
    return known?.village_name || stripped;
  };

  // --- Render --------------------------------------------------------------
  return (
    <FieldShell field={field} error={error}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select
            className="form-input"
            value={district}
            onChange={(e) => {
              setDistrict(e.target.value);
              setSubdistrict('');
              setVillageId('');
            }}
            disabled={districtsQuery.isLoading}
          >
            <option value="">
              {districtsQuery.isLoading ? 'Loading districts…' : 'Select District'}
            </option>
            {districtNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={subdistrict}
            onChange={(e) => {
              setSubdistrict(e.target.value);
              setVillageId('');
            }}
            disabled={!district || subdistrictsQuery.isLoading}
          >
            <option value="">
              {!district
                ? 'Select Subdistrict'
                : subdistrictsQuery.isLoading
                ? 'Loading…'
                : 'Select Subdistrict'}
            </option>
            {subdistrictNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={villageId}
            onChange={(e) => setVillageId(e.target.value)}
            disabled={!subdistrict || villagesQuery.isLoading}
          >
            <option value="">
              {!subdistrict
                ? 'Select Village'
                : villagesQuery.isLoading
                ? 'Loading…'
                : 'Select Village'}
            </option>
            {villages.map((v) => (
              <option key={v.village_id} value={v.village_id}>
                {v.village_name}
              </option>
            ))}
          </select>
        </div>

        {allowMultiple && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-rgf-gray-500">
              {selectedValues.length === 0
                ? 'No villages added yet.'
                : `${selectedValues.length} added`}
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              disabled={!villageId}
              onClick={addCurrent}
            >
              Add
            </Button>
          </div>
        )}

        {allowMultiple && selectedValues.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {selectedValues.map((v, idx) => (
              <li
                key={`${v}-${idx}`}
                className="inline-flex items-center gap-1 rounded-full bg-rgf-gray-100 px-3 py-1 text-xs text-rgf-gray-800"
              >
                <span>{labelForChip(v)}</span>
                <button
                  type="button"
                  aria-label="Remove"
                  className="text-rgf-gray-500 hover:text-red-600"
                  onClick={() => removeAt(idx)}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {!allowMultiple &&
          typeof value === 'string' &&
          value.length > 0 &&
          villageId === stripOtherPrefix(value) && (
            <p className="text-xs text-rgf-gray-500">
              Selected:{' '}
              <span className="font-medium text-rgf-gray-700">
                {villages.find((v) => v.village_id === stripOtherPrefix(value))?.village_name ||
                  stripOtherPrefix(value)}
              </span>
            </p>
          )}
      </div>
    </FieldShell>
  );
};
